// 공용 상세 팝업 셸 — 사용자정보/파일정보/멤버/방정보 등 모든 "상세 페이지 팝업"을 한 규격으로 통일.
// 기존에 모달마다 제각각이던 외곽(z-index·backdrop·위치·닫기·애니메이션)을 한 곳에 모은다.
//   · z-index 1000(전역 confirm 은 ModalRoot 2000 유지) · 반투명 backdrop · 중앙/상단 정렬
//   · ESC 닫기 + backdrop 클릭 닫기(옵션) · fade/scale 애니메이션(tokens.css)
//   · 단순 제목바(title) 또는 커스텀 헤더(header 슬롯) · 본문 스크롤 · 선택 footer
// 사용: <DetailModal open={open} onClose={..} title="..">본문</DetailModal>
//   커스텀 헤더(예: 사용자카드 아바타): <DetailModal open onClose={..} header={<...>}>..</DetailModal>

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { Icon } from '../icons';

interface DetailModalProps {
	open: boolean;
	onClose: () => void;
	title?: string;
	header?: ReactNode;          // 주어지면 title 바 대신 사용(커스텀 헤더)
	footer?: ReactNode;
	children: ReactNode;
	width?: number | string;     // px 숫자 또는 CSS 값. 기본 480
	position?: 'center' | 'top'; // top = 상단 정렬(검색/설정류). 기본 center
	closeOnBackdrop?: boolean;   // 기본 true
	testId?: string;
	bodyClassName?: string;
	/** 우하단 핸들로 크기 조절(브라우저 네이티브 resize) — 입력이 많은 팝업용 (2026-08-29 사용자 "팝업이 작다") */
	resizable?: boolean;
	scrollBody?: boolean;        // 기본 true(children 을 스크롤 영역에 래핑). false=모달 자체 flex 레이아웃 보존(헤더/탭/스크롤본문/푸터를 children 이 직접 구성)
	cardClassName?: string;      // 카드 배경/글자색 등. 기본 bg-white. 테마변수 배경(설정 모달 등) 은 여기로 주입.
	/** 🔒 높이 고정 + 상단 정렬 (2026-09-06 원격) — 탭이 있는 팝업(카드 상세 등)에서 탭 전환 때 본문 높이가 바뀌면 center 정렬이
	 *  매번 재중앙화돼 탭·버튼 클릭 위치가 흔들린다. true 면 높이를 고정(내용은 내부 스크롤)하고 상단에 붙여 위치를 항상 같게 유지. */
	fixedHeight?: boolean;
	/** ✕ 를 카드 우상단에 띄운다 — title 없이 본문이 자체 헤더를 그리는 팝업용 (2026-10-08 형 "닫기 버튼이 X 로 안 된 팝업") */
	closeButton?: boolean;
	/** ⤢ 제목줄 ✕ 옆에 최대화/원래 크기 — 화면을 꽉 채워 쓰는 큰 편집 팝업용(카드 상세, 2026-11-02 형). title 제목줄일 때만 */
	maximizable?: boolean;
}

/** ✕ 공통 닫기 — 카드(relative/absolute) 우상단에 띄운다. 직접 만든 팝업·드로어도 같은 모양으로 (2026-10-08 형 "닫기 버튼이 X 로 안 된 팝업") */
export function ModalCloseX({ onClose, className = '' }: { onClose: () => void; className?: string }) {
	return (
		<button type="button" onClick={onClose} aria-label="close" data-testid="modal-close-x"
			className={`absolute right-3 top-3 z-10 rounded-md bg-gray-100 px-2 py-1 text-ui-xs font-semibold text-fg hover:bg-gray-200 ${className}`}>✕</button>
	);
}

export default function DetailModal({
	open, onClose, title, header, footer, children,
	width = 480, position = 'center', closeOnBackdrop = true, testId, bodyClassName = '', scrollBody = true, resizable,
	cardClassName = 'bg-surface', fixedHeight = false, closeButton = false, maximizable = false,
}: DetailModalProps) {
	const { t } = useTranslation();
	const [maxed, setMaxed] = useState(false);
	// ESC + 안드로이드 하드웨어 뒤로가기 공통 (2026-08-07) — 이전엔 ESC 만 처리해서, APK 에서
	// 이 셸을 쓰는 팝업(사용자 카드·설정·방 정보·초대·멤버 정보 등)이 뒤로가기로 닫히지 않고
	// 웹뷰 히스토리를 그대로 빠져나갔다. backStack 등록은 useEscapeClose 안에 있다.
	useEscapeClose(onClose, open);

	if (!open) return null;

	// fixedHeight 면 상단 정렬(탭 전환에도 위치 고정). 그 외엔 요청한 position 을 따른다.
	const align = maxed ? 'items-center' : (fixedHeight || position === 'top') ? 'items-start pt-[6vh]' : 'items-center';
	const cssWidth = typeof width === 'number' ? `min(${width}px, 94vw)` : width;
	// 최대화 — 바깥 여백 8px 만 남기고 꽉. 손으로 끌어 바꾼 크기(resize)는 최대화 동안 무시된다
	const boxStyle = maxed
		? { width: 'calc(100vw - 16px)', height: 'calc(100dvh - 16px)', maxWidth: 'none', maxHeight: 'none', resize: 'none' as const }
		: { width: cssWidth, ...(fixedHeight ? { height: '86dvh' } : {}), ...(resizable ? { resize: 'both' as const, minWidth: 320, minHeight: 280, maxWidth: '94vw', maxHeight: '88dvh' } : {}) };
	return (
		<div
			role="dialog"
			aria-modal="true"
			data-testid={testId}
			onClick={closeOnBackdrop ? onClose : undefined}
			className={`tpt-dialog-backdrop fixed inset-0 z-[1000] flex justify-center bg-slate-900/40 ${maxed ? 'p-2' : 'p-4'} ${align}`}
		>
			<div
				onClick={(e) => e.stopPropagation()}
				style={boxStyle}
				className={`tpt-dialog-content relative flex ${maxed ? '' : 'max-h-[88dvh]'} w-full flex-col overflow-hidden rounded-2xl shadow-2xl ${cardClassName}`}
			>
				{closeButton && <ModalCloseX onClose={onClose} />}
				{header !== undefined ? header : (title !== undefined && (
					<header className="flex items-center gap-2 border-b border-line-subtle px-5 py-3">
						<div className="flex-1 text-ui-base font-bold text-fg">{title}</div>
						{maximizable && (
							<button type="button" onClick={() => setMaxed((v) => !v)} data-testid="modal-maximize"
								title={maxed ? t('preview.restore', { defaultValue: '원래 크기' }) : t('preview.maximize', { defaultValue: '최대화' })}
								aria-label={maxed ? t('preview.restore', { defaultValue: '원래 크기' }) : t('preview.maximize', { defaultValue: '최대화' })}
								className="rounded-md p-1.5 text-fg-muted hover:bg-hover"><Icon name={maxed ? 'minimize' : 'maximize'} size={14} /></button>
						)}
						<button type="button" onClick={onClose} aria-label="close"
							className="rounded-md bg-gray-100 px-2 py-1 text-ui-xs font-semibold text-fg hover:bg-gray-200">✕</button>
					</header>
				))}
				{scrollBody
					? <div className={`flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>
					: children}
				{footer !== undefined && <div className="border-t border-line-subtle px-5 py-3">{footer}</div>}
			</div>
		</div>
	);
}
